import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface Friend {
  id: string;
  user_id: string;
  friend_id: string;
  status: 'pending' | 'accepted' | 'blocked';
  created_at: string;
  friend_profile: {
    user_id: string;
    full_name: string | null;
    avatar_url: string | null;
    preferred_sports: string[] | null;
    skill_level: string | null;
  };
}

export interface FriendRequest {
  id: string;
  user_id: string;
  friend_id: string;
  status: string;
  created_at: string;
  requester_profile: {
    user_id: string;
    full_name: string | null;
    avatar_url: string | null;
  };
}

export function useFriends() {
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [following, setFollowing] = useState<Friend[]>([]);
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [sentRequests, setSentRequests] = useState<FriendRequest[]>([]);
  const [allRelationships, setAllRelationships] = useState<Array<{
    id: string;
    user_id: string;
    friend_id: string;
    status: 'pending' | 'accepted' | 'blocked';
  }>>([]);
  const [optimisticUpdates, setOptimisticUpdates] = useState<{[userId: string]: string}>({});
  const [pendingOperations, setPendingOperations] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFollowing = async () => {
    if (!user) return [];

    try {
      console.log('🔄 fetchFollowing called for user:', user.id);
      
      // Fetch only relationships where I follow others (user_id = me)
      const { data: followingData, error: followingError } = await supabase
        .from('friends')
        .select(`
          id,
          user_id,
          friend_id,
          status,
          created_at
        `)
        .eq('user_id', user.id)
        .eq('status', 'accepted');

      if (followingError) throw followingError;

      console.log('📊 Raw following data from DB:', followingData);

      if (!followingData || followingData.length === 0) {
        console.log('📭 No following relationships found');
        return [];
      }

      // Get friend user IDs
      const friendUserIds = followingData.map(f => f.friend_id);
      console.log('👥 Following user IDs:', friendUserIds);

      // Fetch profiles for people I follow
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url, preferred_sports, skill_level')
        .in('user_id', friendUserIds);

      if (profilesError) throw profilesError;

      // Map following relationships with profiles
      const followingWithProfiles = followingData.map(friendship => {
        const profile = profilesData?.find(p => p.user_id === friendship.friend_id);
        
        return {
          ...friendship,
          status: friendship.status as 'pending' | 'accepted' | 'blocked',
          friend_profile: profile || {
            user_id: friendship.friend_id,
            full_name: null,
            avatar_url: null,
            preferred_sports: null,
            skill_level: null
          }
        };
      });

      console.log('✅ Final following list with profiles:', followingWithProfiles.map(f => ({ 
        id: f.id, 
        friend_id: f.friend_id, 
        name: f.friend_profile.full_name 
      })));

      return followingWithProfiles;
    } catch (err) {
      console.error('❌ Error fetching following:', err);
      return [];
    }
  };

  const fetchFriends = async () => {
    if (!user) return;

    try {
      // ONLY fetch people I follow (where current user is user_id)
      // This makes the system truly unidirectional like Instagram
      const { data: friendsData, error: friendsError } = await supabase
        .from('friends')
        .select(`
          id,
          user_id,
          friend_id,
          status,
          created_at
        `)
        .eq('user_id', user.id)
        .eq('status', 'accepted');

      if (friendsError) throw friendsError;

      // Get friend user IDs (people I follow)
      const friendUserIds = (friendsData || []).map(f => f.friend_id);

      if (friendUserIds.length === 0) {
        setFriends([]);
      } else {
        // Fetch profiles for people I follow
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url, preferred_sports, skill_level')
          .in('user_id', friendUserIds);

        if (profilesError) throw profilesError;

        // Map friendships with profiles
        const friendsWithProfiles = (friendsData || []).map(friendship => {
          const profile = profilesData?.find(p => p.user_id === friendship.friend_id);
          
          return {
            ...friendship,
            status: friendship.status as 'pending' | 'accepted' | 'blocked',
            friend_profile: profile || {
              user_id: friendship.friend_id,
              full_name: null,
              avatar_url: null,
              preferred_sports: null,
              skill_level: null
            }
          };
        });

        setFriends(friendsWithProfiles);
      }

      // Fetch ALL relationships for relationship status detection
      const { data: allRelationshipsData, error: allRelationshipsError } = await supabase
        .from('friends')
        .select('id, user_id, friend_id, status')
        .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`);

      if (allRelationshipsError) {
        console.error('Error fetching all relationships:', allRelationshipsError);
      } else {
        setAllRelationships((allRelationshipsData || []).map(rel => ({
          ...rel,
          status: rel.status as 'pending' | 'accepted' | 'blocked'
        })));
      }

      // Fetch pending friend requests (where current user is the recipient)
      const { data: requestsData, error: requestsError } = await supabase
        .from('friends')
        .select(`
          id,
          user_id,
          friend_id,
          status,
          created_at
        `)
        .eq('friend_id', user.id)
        .eq('status', 'pending');

      if (requestsError) throw requestsError;

      // Get requester profiles
      const requesterIds = requestsData?.map(r => r.user_id) || [];
      const { data: requesterProfiles, error: requesterProfilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', requesterIds);

      if (requesterProfilesError) throw requesterProfilesError;

      // Map requests with profiles
      const requestsWithProfiles = requestsData?.map(request => {
        const profile = requesterProfiles?.find(p => p.user_id === request.user_id);
        return {
          ...request,
          requester_profile: profile || {
            user_id: request.user_id,
            full_name: null,
            avatar_url: null
          }
        };
      }) || [];

      setFriendRequests(requestsWithProfiles);

      // Fetch sent friend requests (where current user is the sender)
      const { data: sentRequestsData, error: sentRequestsError } = await supabase
        .from('friends')
        .select(`
          id,
          user_id,
          friend_id,
          status,
          created_at
        `)
        .eq('user_id', user.id)
        .eq('status', 'pending');

      if (sentRequestsError) throw sentRequestsError;

      // Get recipient profiles for sent requests
      const recipientIds = sentRequestsData?.map(r => r.friend_id) || [];
      const { data: recipientProfiles, error: recipientProfilesError } = await supabase
        .from('profiles')
        .select('user_id, full_name, avatar_url')
        .in('user_id', recipientIds);

      if (recipientProfilesError) throw recipientProfilesError;

      // Map sent requests with profiles
      const sentRequestsWithProfiles = sentRequestsData?.map(request => {
        const profile = recipientProfiles?.find(p => p.user_id === request.friend_id);
        return {
          ...request,
          requester_profile: profile || {
            user_id: request.friend_id,
            full_name: null,
            avatar_url: null
          }
        };
      }) || [];

      setSentRequests(sentRequestsWithProfiles);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch friends');
    } finally {
      setLoading(false);
    }
  };

  const followUser = async (friendUserId: string) => {
    if (!user) return { error: 'User not authenticated' };

    try {
      // Optimistic update: assume following immediately
      setOptimisticUpdates(prev => ({ ...prev, [friendUserId]: 'i_follow_them' }));
      
      // Check if I already follow them (user.id -> friendUserId)
      const { data: existingRelationship, error: checkError } = await supabase
        .from('friends')
        .select('id, status')
        .eq('user_id', user.id)
        .eq('friend_id', friendUserId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw checkError;
      }

      if (existingRelationship) {
        // I already follow them, nothing to do
        await fetchFriends();
        return { error: null };
      }

      // Create new follow relationship with accepted status immediately
      const { error: insertError } = await supabase
        .from('friends')
        .insert({
          user_id: user.id,
          friend_id: friendUserId,
          status: 'accepted'
        });

      if (insertError) {
        throw insertError;
      }

      // Send follow notification
      const { data: followerProfile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('user_id', user.id)
        .single();

      await supabase
        .from('notifications')
        .insert({
          user_id: friendUserId,
          type: 'follow_notification',
          title: 'Te han comenzado a seguir',
          message: `${followerProfile?.full_name || 'Un usuario'} te ha comenzado a seguir`,
          action_url: `/profile?tab=friends&follower_id=${user.id}`
        });

      await fetchFriends();
      
      // Clear optimistic update
      setOptimisticUpdates(prev => {
        const updated = { ...prev };
        delete updated[friendUserId];
        return updated;
      });
      
      return { error: null };
    } catch (err) {
      // Clear optimistic update on error
      setOptimisticUpdates(prev => {
        const updated = { ...prev };
        delete updated[friendUserId];
        return updated;
      });
      
      return { error: err instanceof Error ? err.message : 'Failed to follow user' };
    }
  };

  // This function is no longer needed since follows are immediate


  // This function is no longer needed since follows are immediate

  // This function is no longer needed since follows are immediate

  const removeFriend = async (friendshipId: string) => {
    try {
      const { error } = await supabase
        .from('friends')
        .delete()
        .eq('id', friendshipId);

      if (error) throw error;

      await fetchFriends();
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to remove friend' };
    }
  };

  const unfollowUser = async (friendUserId: string) => {
    if (!user) return { error: 'User not authenticated' };
    
    // Prevent concurrent operations on the same user
    if (pendingOperations.has(friendUserId)) {
      console.log('🚫 Operation already in progress for user:', friendUserId);
      return { error: 'Operation already in progress' };
    }
    
    try {
      console.log('🔍 Starting unfollow operation for user:', friendUserId);
      
      // Mark operation as pending
      setPendingOperations(prev => new Set([...prev, friendUserId]));
      
      // Store original state for potential rollback
      const originalFollowing = following;
      
      // Optimistically remove from following list with enhanced logging
      setFollowing(prev => {
        const filtered = prev.filter(friend => friend.friend_profile.user_id !== friendUserId);
        console.log('📝 Optimistic update:', {
          original_count: prev.length,
          new_count: filtered.length,
          removed_user: friendUserId,
          removed_user_exists: prev.some(f => f.friend_profile.user_id === friendUserId)
        });
        return filtered;
      });
      
      // Verify the relationship exists before deletion
      console.log('🔍 Verifying relationship exists in DB before deletion');
      const { data: existingRelation, error: checkError } = await supabase
        .from('friends')
        .select('id, user_id, friend_id, status')
        .eq('user_id', user.id)
        .eq('friend_id', friendUserId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('❌ Error checking existing relationship:', checkError);
        throw checkError;
      }

      if (!existingRelation) {
        console.log('⚠️ No relationship found to delete - user may already be unfollowed');
        setPendingOperations(prev => {
          const updated = new Set(prev);
          updated.delete(friendUserId);
          return updated;
        });
        return { error: null };
      }

      console.log('🗑️ Deleting relationship from DB:', {
        relation_id: existingRelation.id,
        user_id: user.id,
        friend_id: friendUserId,
        status: existingRelation.status
      });
      
      // Perform deletion with verification
      const { error: deleteError, count } = await supabase
        .from('friends')
        .delete({ count: 'exact' })
        .eq('user_id', user.id)
        .eq('friend_id', friendUserId);

      if (deleteError) {
        console.error('❌ DB deletion error:', deleteError);
        throw deleteError;
      }
      
      console.log('✅ DB deletion completed:', {
        deleted_count: count,
        expected_count: 1,
        success: count === 1
      });

      // Verify deletion was successful
      const { data: verifyData, error: verifyError } = await supabase
        .from('friends')
        .select('id')
        .eq('user_id', user.id)
        .eq('friend_id', friendUserId);

      if (verifyError) {
        console.error('❌ Error verifying deletion:', verifyError);
        throw verifyError;
      }

      if (verifyData && verifyData.length > 0) {
        console.error('❌ Deletion verification failed - relationship still exists');
        throw new Error('Deletion was not successful');
      }

      console.log('✅ Deletion verified - relationship no longer exists in DB');

      // Clear the pending operation
      setPendingOperations(prev => {
        const updated = new Set(prev);
        updated.delete(friendUserId);
        return updated;
      });
      
      return { error: null };
    } catch (err) {
      console.error('❌ Unfollow operation failed:', err);
      
      // Clear pending operation
      setPendingOperations(prev => {
        const updated = new Set(prev);
        updated.delete(friendUserId);
        return updated;
      });
      
      // Revert optimistic update by fetching fresh data
      console.log('🔄 Reverting optimistic update due to error');
      try {
        const freshFollowing = await fetchFollowing();
        setFollowing(freshFollowing);
      } catch (revertError) {
        console.error('❌ Error reverting optimistic update:', revertError);
      }
      
      return { error: err instanceof Error ? err.message : 'Failed to unfollow user' };
    }
  };

  useEffect(() => {
    if (user) {
      const initializeData = async () => {
        await fetchFriends();
        const followingData = await fetchFollowing();
        setFollowing(followingData);
      };
      
      initializeData();

      // Set up real-time subscription for friends with improved conflict handling
      const channel = supabase
        .channel('friends-changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'friends'
          },
          async (payload: any) => {
            console.log('🔔 Real-time event received:', {
              event: payload.eventType,
              new_data: payload.new,
              old_data: payload.old,
              pending_operations: Array.from(pendingOperations)
            });

            // Check if this change involves the current user
            const isUserInvolved = (payload.new && (payload.new.user_id === user.id || payload.new.friend_id === user.id)) ||
                                  (payload.old && (payload.old.user_id === user.id || payload.old.friend_id === user.id));

            if (!isUserInvolved) {
              console.log('🚫 Real-time event not relevant to current user');
              return;
            }

            // Check if there are pending operations that might conflict
            let affectedUserId = null;
            if (payload.new && payload.new.user_id === user.id) {
              affectedUserId = payload.new.friend_id;
            } else if (payload.new && payload.new.friend_id === user.id) {
              affectedUserId = payload.new.user_id;
            } else if (payload.old && payload.old.user_id === user.id) {
              affectedUserId = payload.old.friend_id;
            } else if (payload.old && payload.old.friend_id === user.id) {
              affectedUserId = payload.old.user_id;
            }

            if (affectedUserId && pendingOperations.has(affectedUserId)) {
              console.log('⏰ Delaying real-time update due to pending operation for user:', affectedUserId);
              // Delay the update to avoid conflicts with pending operations
              setTimeout(async () => {
                if (!pendingOperations.has(affectedUserId)) {
                  console.log('🔄 Executing delayed real-time update for user:', affectedUserId);
                  await fetchFriends();
                  const followingData = await fetchFollowing();
                  setFollowing(followingData);
                }
              }, 2000);
              return;
            }

            console.log('🔄 Processing real-time update immediately');
            await fetchFriends();
            const followingData = await fetchFollowing();
            setFollowing(followingData);
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user, pendingOperations]);

  // Memoized relationship status with optimistic updates - fixed for unidirectional follows
  const getRelationshipStatus = useCallback((targetUserId: string) => {
    if (!user || !targetUserId) return 'none';
    
    // Check for optimistic update first
    if (optimisticUpdates[targetUserId]) {
      return optimisticUpdates[targetUserId];
    }
    
    // Use allRelationships to get accurate bidirectional status
    const relationshipsWithTarget = allRelationships.filter(f => 
      (f.user_id === user.id && f.friend_id === targetUserId) ||
      (f.user_id === targetUserId && f.friend_id === user.id)
    );
    
    // Check if I follow them (user_id = me, friend_id = them)
    const iFollowThem = relationshipsWithTarget.some(f => 
      f.user_id === user.id && f.friend_id === targetUserId && f.status === 'accepted'
    );
    
    // Check if they follow me (user_id = them, friend_id = me)  
    const theyFollowMe = relationshipsWithTarget.some(f => 
      f.user_id === targetUserId && f.friend_id === user.id && f.status === 'accepted'
    );
    
    // Determine relationship status - truly unidirectional like Instagram
    if (iFollowThem && theyFollowMe) {
      return 'mutual_following'; // Both follow each other (true friends)
    } else if (iFollowThem) {
      return 'i_follow_them'; // I follow them only
    } else if (theyFollowMe) {
      return 'they_follow_me'; // They follow me only  
    }
    
    return 'none';
  }, [user, allRelationships, optimisticUpdates]);

  // Helper function to clean up old follow notifications when relationship becomes mutual
  const cleanupOldFollowNotifications = async (userId1: string, userId2: string) => {
    try {
      // Mark as read all follow notifications between these two users
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('type', 'follow_notification')
        .or(`and(user_id.eq.${userId1},action_url.ilike.%follower_id=${userId2}%),and(user_id.eq.${userId2},action_url.ilike.%follower_id=${userId1}%)`);

      if (error) {
        // Don't fail on cleanup errors
      }
    } catch (err) {
      // Don't fail on cleanup errors
    }
  };

  return {
    friends,
    following,
    friendRequests,
    sentRequests,
    loading,
    error,
    followUser,
    removeFriend,
    unfollowUser,
    getRelationshipStatus,
    refetch: fetchFriends,
    fetchFollowing
  };
}